import {
	ButtonWithSpinner,
	FormFieldControl,
	Input,
	ToastCustom,
} from "@components";
import {
	checklistLinks,
	checklistStartAnswer,
} from "@features/checklist/services";
import { zodResolver } from "@hookform/resolvers/zod";
import Clarity from "@microsoft/clarity";
import { useMutation, useQuery } from "@tanstack/react-query";
import { isUUID, maskCPF } from "@utils";
import { Controller, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Form } from "@/components/base/form/form";
import { CarIcon } from "@/components/ui/icons/CarIcon";
import { Image } from "@/components/ui/image";
import { UI_TEXT_LOGIN } from "../constants";
import { checklist_login_schema } from "../schema";
import { useChecklistProgressStore, useChecklistStore } from "../store";
import type { ILoginForm } from "../types";

const defaultValues: ILoginForm = {
	cpf: "",
	plate: "",
};

export const ChecklistLogin = () => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("id") || "";

	const setChecklist = useChecklistStore((state) => state.setChecklist);
	const setCurrentStep = useChecklistProgressStore(
		(state) => state.setCurrentStep
	);
	const setTotalSteps = useChecklistProgressStore(
		(state) => state.setTotalSteps
	);

	const { data } = useQuery({
		queryKey: ["checklist:links", { token, isUUID: isUUID(token) }],
		queryFn: () => checklistLinks({ token, isUUID: isUUID(token) }),
		enabled: !!token,
	});

	const { mutate, isPending } = useMutation({
		mutationKey: ["checklist:start"],
		mutationFn: checklistStartAnswer,
		onSuccess: (dataResponse, variables) => {
			if (token && data && dataResponse) {
				Clarity.identify(variables.cpf);
				Clarity.setTag("tenantName", "itamob");

				setChecklist({
					linkData: {
						...data,
						token: token || undefined,
					},
					answerData: dataResponse,
				});
				if (dataResponse.summary) {
					setCurrentStep(0);
				}
				if (dataResponse.checklistAnswer?.sections?.length) {
					const sectionsWithGeneralDataScreen =
						dataResponse.checklistAnswer?.sections?.length + 2;
					setTotalSteps(sectionsWithGeneralDataScreen);
				}
			}
		},
		onError: (error) => {
			if (error?.message) {
				toast(<ToastCustom description={error?.message} title="Erro" />);
			}
		},
	});

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<ILoginForm>({
		defaultValues,
		resolver: zodResolver(checklist_login_schema),
		mode: "onSubmit",
	});

	const onSubmit = (formData: ILoginForm) => {
		if (data?.checklistId && data?.templateId) {
			mutate({
				...formData,
				checklistId: data?.checklistId,
				templateId: data?.templateId,
			});
		}
	};

	return (
		<div className="flex h-dvh min-h-fit w-full flex-col overflow-y-scroll">
			<Image
				alt="Imagem de veículo"
				className="h-[41dvh] w-full object-cover object-top"
				height={360}
				priority={true}
				src="/checklist-assets/login-cover.png"
				width={100}
			/>
			<div className="relative h-[55dvh] w-full rounded-t-3xl bg-white px-4">
				<div className="-top-[31px] absolute left-0 h-[4dvh] max-h-8 min-h-8 w-full rounded-t-3xl bg-white" />

				<div className="mx-auto flex h-full w-full max-w-[456px] flex-col gap-5">
					<div className="mx-auto flex w-full flex-col gap-3 text-center">
						<div className="mx-auto w-fit rounded-full bg-utility-brand-100_alt p-4">
							<CarIcon className="size-10 text-brand-secondary" />
						</div>

						<div className="flex flex-col gap-1">
							<h1 className="font-bold text-secondary text-xl">
								{data?.checklistName || UI_TEXT_LOGIN.title}
							</h1>
							{data?.checklistDescription ? (
								<p className="text-sm text-tertiary">
									{data.checklistDescription}
								</p>
							) : (
								<p className="text-sm text-tertiary">
									{UI_TEXT_LOGIN.subtitle}
								</p>
							)}
						</div>
					</div>

					<Form
						className="flex flex-1 flex-col gap-3"
						onSubmit={handleSubmit(onSubmit)}
					>
						<Controller
							control={control}
							name="cpf"
							render={({ field: { value, onChange } }) => (
								<FormFieldControl
									error={errors.cpf?.message}
									htmlFor="cpf"
									isRequired
									label={UI_TEXT_LOGIN.cpfLabel}
								>
									<Input
										aria-label="cpf"
										autoComplete="off"
										error={!!errors.cpf?.message}
										inputMode="numeric"
										maxLength={14}
										onChange={(e) => {
											const raw = e.target.value.replace(/\D/g, "");
											onChange(raw.slice(0, 11));
										}}
										placeholder={UI_TEXT_LOGIN.cpfPlaceholder}
										type="text"
										value={maskCPF(value)}
									/>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="plate"
							render={({ field: { onChange, value } }) => (
								<FormFieldControl
									error={errors.plate?.message}
									htmlFor="plate"
									isRequired
									label={UI_TEXT_LOGIN.plateLabel}
								>
									<Input
										aria-label="plate"
										autoCapitalize="characters"
										error={!!errors.plate?.message}
										maxLength={7}
										onChange={(e) => {
											const raw = e.target.value
												.toUpperCase()
												.replace(/[^A-Z0-9]/g, "")
												.slice(0, 7);

											onChange(raw);
										}}
										placeholder={UI_TEXT_LOGIN.platePlaceholder}
										value={value}
									/>
								</FormFieldControl>
							)}
						/>

						<div className="mt-auto mb-8 w-full">
							<ButtonWithSpinner
								className="mt-4 w-full"
								isLoading={isPending}
								type="submit"
							>
								{UI_TEXT_LOGIN.submitButton}
							</ButtonWithSpinner>
						</div>
					</Form>
				</div>
			</div>
		</div>
	);
};
